import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  TimePicker,
  Switch,
  Space,
  message,
  Typography,
  Divider,
  Table,
  Tag,
  Statistic
} from 'antd';
import {
  ShopOutlined,
  DollarOutlined,
  UserOutlined,
  ThunderboltOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { stationOwnerAPI, dataEntryAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface Station {
  _id: string;
  name: string;
  address: string;
  phone: string;
  services: any[];
  workingHours: any;
  electricChargers?: any[];
}

const StationManagement: React.FC = () => {
  const [stations, setStations] = useState<any[]>([]);
  const [selectedStationId, setSelectedStationId] = useState<string>('');
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [fuelPrices, setFuelPrices] = useState<any[]>([]);
  const [evPrices, setEvPrices] = useState<any[]>([]);
  const [stocks, setStocks] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    if (selectedStationId) {
      loadStationData();
    }
  }, [selectedStationId]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
      if (response.length > 0 && !selectedStationId) {
        setSelectedStationId(response[0]._id);
      }
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const loadStationData = async () => {
    if (!selectedStationId) return;
    
    setLoading(true);
    try {
      const [stationData, fuelData, evData, stockData, employeeData] = await Promise.all([
        stationOwnerAPI.getStations().then(stations => 
          stations.find((s: any) => s._id === selectedStationId)
        ),
        dataEntryAPI.getFuelPrices(selectedStationId),
        dataEntryAPI.getEVChargingPrices(selectedStationId),
        dataEntryAPI.getStocks(selectedStationId),
        dataEntryAPI.getEmployees({ stationId: selectedStationId })
      ]);

      setSelectedStation(stationData || null);
      setFuelPrices(fuelData || []);
      setEvPrices(evData || []);
      setStocks(stockData?.stocks || []);
      setEmployees(employeeData || []);

      if (stationData) {
        form.setFieldsValue({
          name: stationData.name,
          address: stationData.address,
          phone: stationData.phone,
          services: stationData.services?.map((s: any) => s.type) || []
        });
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    if (!selectedStationId) return;

    try {
      await stationOwnerAPI.updateStation(selectedStationId, values);
      message.success('İstasyon bilgileri güncellendi');
      loadStationData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Güncelleme başarısız');
    }
  };

  const fuelPriceColumns = [
    {
      title: 'Yakıt Tipi',
      dataIndex: 'fuelType',
      key: 'fuelType',
      render: (type: string) => {
        const labels: any = {
          benzin: 'Benzin',
          diesel: 'Dizel',
          lpg: 'LPG',
          premium_benzin: 'Premium Benzin',
          premium_diesel: 'Premium Dizel'
        };
        return <Tag color="blue">{labels[type] || type}</Tag>;
      }
    },
    {
      title: 'Fiyat',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: any) => (
        <span style={{ fontWeight: 'bold' }}>
          {price.toLocaleString('tr-TR')} ₺ / {record.unit}
        </span>
      )
    },
    {
      title: 'Geçerlilik',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      render: (date: string) => dayjs(date).format('DD MMM YYYY')
    }
  ];

  const evPriceColumns = [
    {
      title: 'Şarj Tipi',
      dataIndex: 'chargerType',
      key: 'chargerType',
      render: (type: string) => <Tag color="purple">{type}</Tag>
    },
    {
      title: 'Güç',
      dataIndex: 'power',
      key: 'power',
      render: (power: number) => `${power} kW`
    },
    {
      title: 'Fiyat',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: any) => (
        <span style={{ fontWeight: 'bold' }}>
          {price.toLocaleString('tr-TR')} ₺ / {record.unit}
        </span>
      )
    }
  ];

  const stockColumns = [
    {
      title: 'Ürün',
      dataIndex: 'productName',
      key: 'productName'
    },
    {
      title: 'Stok',
      key: 'quantity',
      render: (record: any) => (
        <span style={{
          color: record.quantity <= record.minStockLevel ? '#ff4d4f' : '#000'
        }}>
          {record.quantity} {record.unit}
        </span>
      )
    },
    {
      title: 'Min. Stok',
      dataIndex: 'minStockLevel',
      key: 'minStockLevel',
      render: (level: number, record: any) => `${level} ${record.unit}`
    }
  ];

  const employeeColumns = [
    {
      title: 'Ad Soyad',
      key: 'name',
      render: (record: any) => `${record.firstName} ${record.lastName}`
    },
    {
      title: 'Pozisyon',
      dataIndex: 'position',
      key: 'position',
      render: (position: string) => {
        const labels: any = {
          manager: 'Müdür',
          cashier: 'Kasiyer',
          pump_attendant: 'Pompa Görevlisi',
          technician: 'Teknisyen',
          security: 'Güvenlik',
          cleaner: 'Temizlik',
          other: 'Diğer'
        };
        return <Tag>{labels[position] || position}</Tag>;
      }
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    }
  ];

  if (!selectedStation) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>Lütfen bir istasyon seçin</Title>
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: '24px' }}>
            <ShopOutlined /> Kapsamlı İstasyon Yönetimi
          </Title>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Select
            value={selectedStationId}
            onChange={setSelectedStationId}
            style={{ width: '100%', maxWidth: 300 }}
            size="large"
          >
            {stations.map((station: any) => (
              <Option key={station._id} value={station._id}>
                {station.name}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {/* Özet İstatistikler */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Yakıt Fiyatları"
              value={fuelPrices.length}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="EV Şarj Fiyatları"
              value={evPrices.length}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Stok Ürünleri"
              value={stocks.length}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Personel"
              value={employees.length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs defaultActiveKey="info">
          <TabPane tab="Temel Bilgiler" key="info">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="İstasyon Adı"
                    rules={[{ required: true, message: 'İstasyon adı giriniz' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label="Telefon"
                    rules={[{ required: true, message: 'Telefon giriniz' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="address"
                label="Adres"
                rules={[{ required: true, message: 'Adres giriniz' }]}
              >
                <TextArea rows={2} />
              </Form.Item>

              <Form.Item
                name="services"
                label="Hizmetler"
              >
                <Select mode="multiple" placeholder="Hizmetler seçin">
                  <Option value="fuel">Yakıt</Option>
                  <Option value="market">Market</Option>
                  <Option value="car_wash">Oto Yıkama</Option>
                  <Option value="restaurant">Restoran</Option>
                  <Option value="atm">ATM</Option>
                  <Option value="ev_charging">Elektrikli Araç Şarjı</Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit">
                  Kaydet
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="Yakıt Fiyatları" key="fuel">
            <Table
              columns={fuelPriceColumns}
              dataSource={fuelPrices}
              rowKey="_id"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </TabPane>

          <TabPane tab="EV Şarj Fiyatları" key="ev">
            <Table
              columns={evPriceColumns}
              dataSource={evPrices}
              rowKey="_id"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </TabPane>

          <TabPane tab="Stok Durumu" key="stock">
            <Table
              columns={stockColumns}
              dataSource={stocks}
              rowKey="_id"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </TabPane>

          <TabPane tab="Personel" key="employees">
            <Table
              columns={employeeColumns}
              dataSource={employees}
              rowKey="_id"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default StationManagement;

